import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Drawer, Avatar, Text } from '@Sage/hoi-poi-ui';
import { COMPANIES, CONTACTS } from 'constants/Entities';
import { EntityFiltersActions, EntityListSelectActions } from 'actions';
import DrawerTabs from 'components/DrawerTabs';
import { FILTER_KEY as SUBSCRIBERS_COMPANIES_FILTER_KEY } from './CompaniesTableConfig';
import { FILTER_KEY as SUBSCRIBERS_CONTACTS_FILTER_KEY } from './ContactsTableConfig';
import { getLiteral } from 'utils/getLiteral';
import { intercomHideAndShow } from 'utils/intercom';
import { userflowHideAndShow } from 'utils/userflow';
import BulkMarketingSubscribedConfirmModal from '../bulkSubscribe/BulkMarketingSubscribedConfirmModal';
import useBulkMarketingSubscribedConfirm from '../bulkSubscribe/useBulkMarketingSubscribedConfirm';
import CompaniesTab from './CompaniesTab';
import ContactsTab from './ContactsTab';

import './styles.scss';

const TAB_COMPANIES = 'companies';
const TAB_CONTACTS = 'contacts';

const DRAWER_CONTENT_STYLES = { maxWidth: '85vw', height: '100%' };

const TabTitle = ({ icon, label }) => (
    <div className="fm-subscribers-panel__tab-title">
        <Avatar icon={icon} className="fm-subscribers-panel__tab-avatar" />
        <Text isTruncated>{label}</Text>
    </div>
);

const TAB_ENTITY_MAP = {
    [TAB_COMPANIES]: COMPANIES,
    [TAB_CONTACTS]: CONTACTS,
};

const Subscribers = memo(
    ({
        isOpen,
        onClose,
        setActiveEntity,
        resetListSelect,
        setShouldClearWeb5Checkboxes,
        hideFilters,
    }) => {
        const {
            confirmState,
            handleOpenBulkSubscribedConfirm,
            handleCloseBulkSubscribedConfirm,
            handleConfirmBulkSubscribed,
        } = useBulkMarketingSubscribedConfirm({
            resetListSelect,
            setShouldClearWeb5Checkboxes,
        });

        const [listReady, setListReady] = useState(false);
        const [listSessionKey, setListSessionKey] = useState(0);

        // On open: wipe any previous/foreign selection for both entities before the
        // lists render, so the Subscribers grids always start from a clean state.
        useLayoutEffect(() => {
            if (!isOpen) {
                setListReady(false);
                return;
            }

            resetListSelect(COMPANIES, true);
            resetListSelect(CONTACTS, true);
            setActiveEntity(COMPANIES);
            setListSessionKey((key) => key + 1);
            setListReady(true);
        }, [isOpen, resetListSelect, setActiveEntity]);

        // Runs after the grids mount/init, so the clear flag is not overwritten by
        // NewEntityList's own selection init. Forces the Web5 checkboxes to clear.
        useEffect(() => {
            if (!isOpen || !listReady) return;
            setShouldClearWeb5Checkboxes(COMPANIES, true);
            setShouldClearWeb5Checkboxes(CONTACTS, true);
        }, [isOpen, listReady, listSessionKey, setShouldClearWeb5Checkboxes]);

        useEffect(() => {
            if (!isOpen) return undefined;

            userflowHideAndShow('hide');
            intercomHideAndShow('hide');
            hideFilters(COMPANIES, SUBSCRIBERS_COMPANIES_FILTER_KEY);
            hideFilters(CONTACTS, SUBSCRIBERS_CONTACTS_FILTER_KEY);

            return () => {
                userflowHideAndShow('show');
                intercomHideAndShow('show');
                resetListSelect(COMPANIES, true);
                resetListSelect(CONTACTS, true);
            };
        }, [isOpen, hideFilters, resetListSelect]);

        const handleTabChange = useCallback(
            (key) => {
                const entity = TAB_ENTITY_MAP[key];
                if (entity) setActiveEntity(entity);
            },
            [setActiveEntity],
        );

        const tabs = useMemo(
            () => [
                {
                    key: TAB_COMPANIES,
                    title: <TabTitle icon="accounts" label={getLiteral('label_accounts')} />,
                    content: (
                        <CompaniesTab
                            listSessionKey={listSessionKey}
                            onRequestBulkSubscribedConfirm={handleOpenBulkSubscribedConfirm}
                        />
                    ),
                },
                {
                    key: TAB_CONTACTS,
                    title: <TabTitle icon="contacts" label={getLiteral('label_contacts')} />,
                    content: (
                        <ContactsTab
                            listSessionKey={listSessionKey}
                            onRequestBulkSubscribedConfirm={handleOpenBulkSubscribedConfirm}
                        />
                    ),
                },
            ],
            [handleOpenBulkSubscribedConfirm, listSessionKey],
        );

        if (!isOpen) return null;

        return (
            <Drawer
                isOpen={isOpen}
                onRequestClose={onClose}
                className="fm-subscribers-panel"
                width="1150px"
                contentStyles={DRAWER_CONTENT_STYLES}
                shouldCloseOnEsc={true}
                shouldCloseOnOverlayClick={false}
            >
                {listReady && (
                    <DrawerTabs
                        className="fm-subscribers-panel__inner"
                        panelsClassName="fm-subscribers-panel__panels"
                        tabs={tabs}
                        defaultActiveTab={TAB_COMPANIES}
                        onClose={onClose}
                        onTabChange={handleTabChange}
                    />
                )}
                <BulkMarketingSubscribedConfirmModal
                    isOpen={confirmState.isOpen}
                    isSubmitting={confirmState.isSubmitting}
                    payload={confirmState.payload}
                    onConfirm={handleConfirmBulkSubscribed}
                    onClose={handleCloseBulkSubscribedConfirm}
                />
            </Drawer>
        );
    },
);

const mapDispatchToProps = (dispatch) => ({
    setActiveEntity: bindActionCreators(EntityListSelectActions, dispatch).setActive,
    resetListSelect: bindActionCreators(EntityListSelectActions, dispatch).resetListSelect,
    setShouldClearWeb5Checkboxes: bindActionCreators(EntityListSelectActions, dispatch)
        .setShouldClearWeb5Checkboxes,
    hideFilters: bindActionCreators(EntityFiltersActions, dispatch).hideFilters,
});

export default connect(null, mapDispatchToProps)(Subscribers);
